import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DataTable, { Column } from './DataTable';
import EmptyState from './EmptyState';
import PageBar from './ui/PageBar';
import FilterPopover from './ui/FilterPopover';
import LoadErrorBox from './ui/LoadErrorBox';
import StatusBadge from './ui/StatusBadge';
import { useLoad } from '../hooks/useLoad';
import { usePageRefresh } from '../hooks/usePageRefresh';
import { loadActionLog, describeActor, ActionLogEntry } from '../services/ActionLogService';
import { formatDateTime } from '../utils/formatDate';
import { actionLabel, actionVariant, describeChange, entityLabel, entityLink } from '../utils/historyLabels';
import '../styles/components/HistoryPage.css';

/** An action-log entry in the words the table shows. DataTable's header search scans these fields, so what is typed matches what is read. */
export interface HistoryRow {
  id: string;
  createdAt: string;
  when: string;
  action: string;
  actionText: string;
  entity: string;
  actor: string;
  details: string;
  path: string;
}

export const toHistoryRow = (e: ActionLogEntry): HistoryRow => ({
  id: e.id,
  createdAt: e.createdAt,
  when: formatDateTime(e.createdAt),
  action: e.action,
  actionText: actionLabel(e.action),
  entity: entityLabel(e.entityType),
  actor: describeActor(e),
  details: describeChange(e),
  path: entityLink(e) ?? '',
});

const ALL = '';
const options = (values: string[], allLabel: string) => [
  { value: ALL, label: allLabel },
  ...[...new Set(values)].sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v })),
];

const HistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [action, setAction] = useState(ALL);
  const [entity, setEntity] = useState(ALL);
  const [actor, setActor] = useState(ALL);

  const load = useLoad(() => loadActionLog(500), []);
  usePageRefresh(() => load.reload(), load.loading);

  const rows = useMemo(() => (load.data ?? []).map(toHistoryRow), [load.data]);
  const shown = useMemo(
    () => rows.filter(r => (!action || r.actionText === action) && (!entity || r.entity === entity) && (!actor || r.actor === actor)),
    [rows, action, entity, actor],
  );

  const bar = (
    <PageBar crumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'History' }]}>
      <FilterPopover
        selects={[
          { key: 'action', label: 'Action', options: options(rows.map(r => r.actionText), 'All actions'), value: action, defaultValue: ALL, onChange: setAction },
          { key: 'entity', label: 'Record', options: options(rows.map(r => r.entity), 'All records'), value: entity, defaultValue: ALL, onChange: setEntity, searchable: true },
          { key: 'actor', label: 'Done by', options: options(rows.map(r => r.actor), 'Everyone'), value: actor, defaultValue: ALL, onChange: setActor, searchable: true },
        ]}
      />
    </PageBar>
  );

  if (load.error) {
    return (
      <>
        {bar}
        <div className="wt-container fade-in"><LoadErrorBox what="history" error={load.error} onRetry={load.reload} retrying={load.loading} /></div>
      </>
    );
  }

  const columns: Column<HistoryRow>[] = [
    { key: 'createdAt', header: 'When', sortable: true, width: '170px', render: (_v, r) => r.when },
    {
      key: 'actionText', header: 'Action', sortable: true, width: '110px',
      render: (_v, r) => <StatusBadge variant={actionVariant(r.action)}>{r.actionText}</StatusBadge>,
    },
    {
      key: 'entity', header: 'Record', sortable: true,
      render: (_v, r) => (r.path
        ? <Link className="history-record-link" to={r.path} onClick={e => e.stopPropagation()}>{r.entity}</Link>
        : r.entity),
    },
    { key: 'actor', header: 'Done by', sortable: true },
    { key: 'details', header: 'Details' },
  ];

  const filtered = !!action || !!entity || !!actor;
  return (
    <>
      {bar}
      <div className="wt-container fade-in">
        {!load.loading && rows.length === 0 ? (
          <EmptyState
            icon="clock"
            title="No recorded actions yet"
            description="Every create, update, and delete across the project will show up here as it happens."
          />
        ) : (
          <DataTable<HistoryRow>
            data={shown}
            columns={columns}
            loading={load.loading}
            pagination={{ enabled: true, pageSize: 25, showPageSizeSelector: true, pageSizeOptions: [25, 50, 100] }}
            defaultSort={{ key: 'createdAt', direction: 'desc' }}
            emptyMessage={filtered ? 'No actions match these filters' : 'No actions match your search'}
            onRowClick={r => { if (r.path) navigate(r.path); }}
          />
        )}
      </div>
    </>
  );
};

export default HistoryPage;
